import { Inject, Injectable } from '@angular/core';
import { Salesperson, SalesTeamInterface, SalesTeamSdk, SalesTeamService } from '@vendasta/sales';
import { Observable } from 'rxjs';
import { first, map, pluck } from 'rxjs/operators';
import { ApiService } from '../api-service/api.service';
import { PartnerService } from '../core/partner.service';
import { SalesToolHostService } from '../sales/salestool.host.service';
import { SalesTeam, SalesTeamId } from './sales-team';

@Injectable({ providedIn: 'root' })
export class AjaxSalesTeamsService {
  constructor(
    private api: ApiService,
    private partnerService: PartnerService,
    private salesToolHostService: SalesToolHostService,
    @Inject(SalesTeamService) private salesSdk: SalesTeamSdk,
  ) {}

  list(marketId?: string): Observable<SalesTeam[]> {
    return this.salesSdk.list(this.partnerService.partnerId, marketId, null, 1000).pipe(
      pluck('results'),
      map((results: SalesTeamInterface[]) => {
        if (results) {
          return results.map(
            (t) =>
              ({
                partnerId: t.partnerId,
                marketId: t.marketId,
                groupId: t.groupId,
                teamName: t.teamName,
                updated: t.updated,
              } as SalesTeam),
          );
        }
        return [];
      }),
    );
  }

  getMembers(groupId: string): Observable<string[]> {
    return this.salesSdk.getMembers(groupId).pipe(map((v: Salesperson[]) => v.map((sp) => sp.salespersonId)));
  }

  addMembers(groupId: string, salespersonIds: string[]): Observable<any> {
    const params = {
      groupId: groupId,
      salespersonIds: salespersonIds,
    };
    const path = '/internalApi/v1/sales-teams/add-members/';
    const url = this.salesToolHostService.host() + path;
    return this.api.post(url, params);
  }

  create(marketId: string, teamName: string): Observable<SalesTeamId> {
    const params = {
      partnerId: this.partnerService.partnerId,
      marketId: marketId,
      name: teamName,
    };
    const path = '/internalApi/v1/sales-teams/create/';
    const url = this.salesToolHostService.host() + path;
    return this.api.post(url, params).pipe(
      first(),
      map((salesTeamId) => salesTeamId as string),
    );
  }

  get(salesTeamId: string): Observable<SalesTeam> {
    const params = {
      groupId: salesTeamId,
    };
    const path = '/internalApi/v1/sales-teams/get/';
    const url = this.salesToolHostService.host() + path;
    return this.api.post(url, params).pipe(
      first(),
      map((response) => SalesTeam.fromApi(response.result)),
    );
  }

  removeMember(teamId: string, memberId: string): Observable<null> {
    const params = {
      teamId: teamId,
      salespersonIds: [memberId],
    };
    const path = '/internalApi/v1/sales-teams/remove-members/';
    const url = this.salesToolHostService.host() + path;
    return this.api.post(url, params);
  }

  delete(salesTeamId: string): Observable<null> {
    const params = {
      groupId: salesTeamId,
    };
    const path = '/internalApi/v1/sales-teams/delete/';
    const url = this.salesToolHostService.host() + path;
    return this.api.post(url, params);
  }
}
